<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { getRequest } from '@/Util/utils';
  import { withUnit, fmtDate } from '../PlanReport/format';
  import { decayName, forgetPredictiveState } from './predictive';

  /* eslint-disable @typescript-eslint/no-explicit-any */
  // status_report.predictive of a Goal or Race plan: Predictive Mode is running, and a breakthrough may be waiting for
  // the athlete to apply it to the plan.
  export let predictive: any;
  export let predictiveMode: any = null;
  export let forUser: string | null = null;
  // Which part to show: the breakthrough waiting for an answer, or the small note that the mode is on.
  export let part: 'proposal' | 'notice' = 'proposal';

  const dispatch = createEventDispatcher();
  let showHelp = false;
  let busy = false;
  let message: string | null = null;
  let failed = false;
  let done = false;   // answered: the page reloads, the buttons stay off

  $: proposal = predictive?.proposal;
  $: activity = proposal?.breakthrough;
  // What the question mark says on hover: which activity this is about and what applying it does.
  $: who = `${activity?.name ?? $_('report.predictive.an_activity')}${activity?.date ? ' ' + $_('report.predictive.on_date', { values: { date: fmtDate(activity.date) } }) : ''}`;
  $: tip = $_('report.predictive.tip', { values: { who } });
  const sig = (s: any) => (s ? `TP ${withUnit('W', s.ftp)}, HIE ${withUnit('J', s.hie)}, PP ${withUnit('W', s.pp)}` : '');

  async function post(url: string, body: object = {}) {
    const response = await window.fetch(url, { method: 'POST', ...getRequest(), body: JSON.stringify(body) });
    forgetPredictiveState();
    return response.json();
  }

  async function adjust() {
    busy = true;
    message = null;
    try {
      const result = await post('/predictive-mode/adjust', forUser ? { forUser } : {});
      failed = !result.result;
      message = result.message ?? (failed ? $_('report.predictive.failed') : $_('report.predictive.now_follows'));
      if (!failed) {
        message += ' ' + $_('report.predictive.recalculating');
        done = true;
        dispatch('applied');   // the page refreshes itself when the recalculation is done
      }
    } catch (e) {
      failed = true;
      message = $_('report.predictive.failed');
    } finally {
      busy = false;
    }
  }

  async function notNow() {
    busy = true;
    try {
      await post('/predictive-mode/dismiss');
      done = true;
      dispatch('dismissed');
    } finally {
      busy = false;
    }
  }
</script>

{#if part === 'proposal' && proposal && !forUser}
  <section class="mb-6 rounded border border-green-200 bg-green-50 p-4">
    <h2 class="text-lg font-semibold mb-1">{$_('report.predictive.heading')}</h2>
    <p class="text-sm text-zinc-700 mb-1">
      {$_('report.predictive.shows', { values: { who } })}
      {$_('report.predictive.applying')}
    </p>
    <div class="mt-3 flex items-center gap-2">
      {#if !done}
        <button class="rounded bg-zinc-800 text-white px-3 py-1.5 text-sm" on:click={adjust} disabled={busy}>{busy ? $_('report.predictive.applying_busy') : $_('report.predictive.apply')}</button>
        <button class="rounded border border-zinc-300 bg-white px-3 py-1.5 text-sm" on:click={notNow} disabled={busy}>{$_('report.predictive.not_now')}</button>
      {/if}
      {#if !done}
        <button class="h-6 w-6 rounded-full border border-zinc-400 text-zinc-600 text-sm leading-none" aria-label={$_('report.predictive.what')} aria-expanded={showHelp} title={tip} on:click={() => (showHelp = !showHelp)}>?</button>
      {/if}
    </div>

    {#if showHelp && !done}
      <div class="mt-3 rounded border border-zinc-200 bg-white p-3 text-sm text-zinc-700 space-y-2" role="note">
        {#if activity?.path}
          <p>{$_('report.predictive.the_activity')} <a class="text-blue-600" href="/activities/{activity.path}">{activity.name ?? $_('report.result.activity')}</a>{activity.date ? `, ${fmtDate(activity.date)}` : ''}. {$_('report.predictive.signature_it_shows', { values: { proposed: sig(proposal.proposed), current: sig(proposal.current) } })}</p>
        {/if}
        <p class="font-medium">{$_('report.predictive.what_apply_does_a')} <em>{$_('report.predictive.apply')}</em> {$_('report.predictive.what_apply_does_b')}</p>
        <ul class="list-disc pl-5 space-y-1">
          <li>{$_('report.predictive.li1')}</li>
          <li>{$_('report.predictive.li2')}</li>
          <li>{$_('report.predictive.li3')}</li>
          <li>{$_('report.predictive.li4')}</li>
          <li>{$_('report.predictive.li5_a')} <em>{$_('report.predictive.not_now')}</em> {$_('report.predictive.li5_b')}</li>
        </ul>
      </div>
    {/if}

    {#if message}<p class="mt-3 text-sm {failed ? 'text-red-700' : 'text-green-700'}">{message}</p>{/if}
  </section>
{:else if part === 'notice' && predictive?.mode_on}
  <section class="mb-6 rounded border border-zinc-200 bg-white px-4 py-3">
    <p class="text-sm text-zinc-600"><span class="font-medium text-zinc-800">{$_('report.predictive.mode_on')}</span>
      {predictiveMode?.from ? $_('report.predictive.follows_from', { values: { date: fmtDate(predictiveMode.from) } }) : $_('report.predictive.follows')}
      {$_('report.predictive.notice_a')} <b>{decayName(predictiveMode?.previous_decay_method)}</b> {$_('report.predictive.notice_b')}
    </p>
  </section>
{/if}
